import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  NativeSelect,
  useToast,
} from '@provenance/design';
import { AU_STATES, consoleRegistrationSchema, type ConsoleRegistrationInput } from '@provenance/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toBase64 } from '../configuration/upload';
import { ApiError, api } from '../../lib/api';

/**
 * "Register an exporter" (T-147) — the public registration form, filled in by chamber staff or
 * Provenir Support for the exporter on the phone. It lands in Registrations like any other
 * application and is decided there, so there is one review path, not two.
 */
export function LodgeRegistrationDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<ConsoleRegistrationInput>({
    resolver: zodResolver(consoleRegistrationSchema),
    defaultValues: {
      applicantType: 'EXPORTER',
      address: { state: 'QLD' },
      signatories: [{ fullName: '', position: '', email: null, specimenFilename: '', specimenBase64: '' }],
    } as never,
  });
  const signatories = useFieldArray({ control: form.control, name: 'signatories' });
  const errors = form.formState.errors;

  const lodge = useMutation({
    mutationFn: (values: ConsoleRegistrationInput) => api.post<{ id: string }>('/api/console/registrations', values),
    onSuccess: (r) => {
      toast.success('Registration lodged', 'It is in Registrations, waiting for a decision.');
      void queryClient.invalidateQueries({ queryKey: ['registrations'] });
      onOpenChange(false);
      form.reset();
      navigate(`/registrations/${r.id}`);
    },
    onError: (error) => toast.error('Could not lodge it', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader
          title="Register an exporter"
          description="For an exporter who has asked you to do it. It joins Registrations and is decided there like any application."
        />
        <form onSubmit={form.handleSubmit((v) => lodge.mutate(v))} noValidate>
          <DialogBody>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <Field label="Company name" required error={errors.companyName?.message}>
                <Input {...form.register('companyName')} />
              </Field>
              <Field label="ABN" required error={errors.abn?.message}>
                <Input inputMode="numeric" {...form.register('abn', { setValueAs: (v: string) => v.replace(/\s/g, '') })} />
              </Field>
              <Field label="Applying as" required>
                <NativeSelect {...form.register('applicantType')}>
                  <option value="EXPORTER">Exporter</option>
                  <option value="FREIGHT_FORWARDER">Freight forwarder</option>
                </NativeSelect>
              </Field>
              <Field label="Trading name">
                <Input {...form.register('tradingName')} />
              </Field>
              <Field label="Contact first name" required error={errors.contactFirstName?.message}>
                <Input {...form.register('contactFirstName')} />
              </Field>
              <Field label="Contact last name" required error={errors.contactLastName?.message}>
                <Input {...form.register('contactLastName')} />
              </Field>
              <Field label="Contact email" required error={errors.contactEmail?.message}>
                <Input type="email" {...form.register('contactEmail')} />
              </Field>
              <Field label="Contact phone" required error={errors.contactPhone?.message}>
                <Input type="tel" {...form.register('contactPhone')} />
              </Field>
              <Field label="Street address" required error={errors.address?.line1?.message} className="sm:col-span-2">
                <Input {...form.register('address.line1')} />
              </Field>
              <Field label="Suburb" required error={errors.address?.suburb?.message}>
                <Input {...form.register('address.suburb')} />
              </Field>
              <div className="grid grid-cols-2 gap-3.5">
                <Field label="State" required>
                  <NativeSelect {...form.register('address.state')}>
                    {AU_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Postcode" required error={errors.address?.postcode?.message}>
                  <Input inputMode="numeric" {...form.register('address.postcode')} />
                </Field>
              </div>
            </div>

            <fieldset className="mt-5 flex flex-col gap-3">
              <legend className="mb-1 [font-size:var(--pv-text-xs)] font-semibold">
                People authorised to sign, with a specimen of each signature
              </legend>
              {signatories.fields.map((field, i) => (
                <div key={field.id} className="grid gap-3 rounded-[var(--pv-radius)] border border-[var(--pv-line)] p-3 sm:grid-cols-2">
                  <Field label="Full name" required error={errors.signatories?.[i]?.fullName?.message}>
                    <Input {...form.register(`signatories.${i}.fullName`)} />
                  </Field>
                  <Field label="Position" required error={errors.signatories?.[i]?.position?.message}>
                    <Input {...form.register(`signatories.${i}.position`)} />
                  </Field>
                  <Field label="Email" help="Lets them upload or email certificates.">
                    <Input type="email" {...form.register(`signatories.${i}.email`, { setValueAs: (v: string) => v || null })} />
                  </Field>
                  <Field label="Specimen signature (PNG or JPEG)" required error={errors.signatories?.[i]?.specimenBase64 ? 'Add the specimen' : undefined}>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        form.setValue(`signatories.${i}.specimenFilename`, file.name);
                        form.setValue(`signatories.${i}.specimenBase64`, await toBase64(file), { shouldValidate: true });
                      }}
                    />
                  </Field>
                  {signatories.fields.length > 1 && (
                    <div className="sm:col-span-2">
                      <Button type="button" variant="ghost" size="sm" leadingIcon={<Trash2 className="size-3.5" />} onClick={() => signatories.remove(i)}>
                        Remove this person
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {signatories.fields.length < 10 && (
                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    leadingIcon={<Plus className="size-3.5" />}
                    onClick={() => signatories.append({ fullName: '', position: '', email: null, specimenFilename: '', specimenBase64: '' })}
                  >
                    Add another person
                  </Button>
                </div>
              )}
            </fieldset>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={lodge.isPending}>
              Lodge registration
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
